import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ToastContainer } from './Toast';
import type { ToastType } from './Toast';
import { captureError } from '../../errors/captureError';

type ToastEntry = { id: number; message: string; type: ToastType };

type ToastContextValue = {
    addToast: (message: string, type?: ToastType) => void;
    success: (message: string) => void;
    error: (message: string) => void;
    warning: (message: string) => void;
    info: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let _toastId = 0;

/** Toasts arriving within this window that share the same (message + type)
 *  are swallowed. Prevents rapid-fire error storms (e.g. bulk-delete fails
 *  5× in a row) from stacking five identical toasts on top of each other. */
const DEDUP_WINDOW_MS = 1500;

/**
 * Mounts the toast container at the tree root and exposes addToast/
 * success/error via context so any descendant can fire feedback
 * without prop drilling. One SSOT for user-visible async feedback.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastEntry[]>([]);
    const lastFiredRef = useRef<Map<string, number>>(new Map());

    const removeToast = useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const addToast = useCallback((message: string, type: ToastType = 'success') => {
        // Dedup: if the same (message + type) fired within the last
        // DEDUP_WINDOW_MS, drop this call. The user already sees the
        // earlier toast — a second copy is just visual noise.
        const key = `${type}::${message}`;
        const now = Date.now();
        const last = lastFiredRef.current.get(key) ?? 0;
        if (now - last < DEDUP_WINDOW_MS) return;
        lastFiredRef.current.set(key, now);

        const id = ++_toastId;
        setToasts((prev) => [...prev, { id, message, type }]);
        // Soft haptic confirm on supporting user agents. Feature-detected
        // so Safari / Firefox / older browsers silently no-op.
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            try {
                navigator.vibrate(type === 'error' ? [40, 30, 40] : 20);
            } catch (err) {
                /* some UAs reject inside non-user-gesture calls — ignore */
                captureError(err, { scope: 'ToastProvider', op: 'vibrate' });
            }
        }
    }, []);

    const value = useMemo<ToastContextValue>(() => ({
        addToast,
        success: (msg: string) => addToast(msg, 'success'),
        error: (msg: string) => addToast(msg, 'error'),
        warning: (msg: string) => addToast(msg, 'warning'),
        info: (msg: string) => addToast(msg, 'info'),
    }), [addToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
}

/**
 * Consume the toast context. Throws if called outside the provider
 * so missing wiring fails loudly instead of silently swallowing feedback.
 */
 
export function useToastContext(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (ctx === null) {
        throw new Error('useToastContext must be used inside <ToastProvider>');
    }
    return ctx;
}
